import { debounce } from "lodash-es";
import { executeFormExecutable } from "@/api/form_executables";
import { ref } from "vue";

export function useFormExecutable(formUuid, formExecutable) {
  const isExecutingApiCode = ref(false);

  async function executeApiCode(formElements) {
    if (!formExecutable?.uuid || !formUuid) {
      return;
    }

    isExecutingApiCode.value = true;

    const fieldsData = {};

    // Parse field IDs from executable code if available
    if (formExecutable.code) {
      // Extract all field IDs in {{field_id}} format from the code
      const fieldIdMatches =
        formExecutable.code.match(/\{\{([^}]+)\}\}/g) || [];

      if (fieldIdMatches.length > 0) {
        fieldIdMatches.forEach((match) => {
          // Extract the field ID from the match (remove {{ and }})
          const fieldId = match.replace(/\{\{|\}\}/g, "").trim();
          const field = formElements.find((el) => el.id === fieldId);
          if (field) {
            fieldsData[fieldId] = field.value;
          }
        });
      }
    }

    try {
      const { data } = await executeFormExecutable(
        formUuid,
        formExecutable.uuid,
        { fieldsData },
      );

      formExecutable.sampleData = data.result || {};
    } catch (error) {
      console.debug("Error executing code:", error);
      throw error;
    } finally {
      isExecutingApiCode.value = false;
    }
  }

  const executeApiCodeDebounced = debounce(async function (formElements) {
    await executeApiCode(formElements);
  }, 750);

  async function triggerExecutable(triggerType, formElements, metadata = {}) {
    if (
      !formExecutable?.uuid ||
      !formUuid ||
      formExecutable.triggerPoint?.type !== triggerType
    ) {
      return;
    }

    switch (triggerType) {
      case "on-page-load":
        await executeApiCode(formElements);
        break;
      case "after-submit":
        await executeApiCode(formElements);
        break;
      case "on-field-change":
        if (!metadata.fieldId) {
          console.warn("Field ID is required for on-field-change trigger");
          return;
        }

        if (formExecutable.triggerPoint.fields.includes(metadata.fieldId)) {
          isExecutingApiCode.value = true;
          executeApiCodeDebounced(formElements);
        }
        break;
      default:
        console.warn(`Unknown trigger type: ${triggerType}`);
        break;
    }
  }

  return {
    executeApiCode,
    executeApiCodeDebounced,
    triggerExecutable,
    isExecutingApiCode,
  };
}
